import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../config/api.config';
import { PDFExportButton } from '../../../components/PDFExport';
import './SubmissionDetails.css';

const SubmissionDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get submission ID from params or from state
  const submissionId = id || location.state?.submissionId;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [isMarked, setIsMarked] = useState(false);

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const markSubmission = async (submissionData) => {
    if (submissionData.status === 'pending' && !isMarked) {
      try {
        const response = await axios.post(
          getApiUrl(`/api/student/submissions/mark/${submissionData.classId?._id}/${submissionId}`),
          {},
          { headers: getAuthHeaders() }
        );
        console.log('Marking response:', response.data);
        setIsMarked(true);
        // Update the details with the marked data
        setDetails(prevDetails => ({
          ...prevDetails,
          status: 'marked',
          totalScore: response.data.data.totalScore,
          submissions: response.data.data.results.map((result, index) => ({
            ...prevDetails.submissions[index],
            score: result.score,
            feedback: result.feedback
          }))
        }));
      } catch (err) {
        console.error('Error marking submission:', err);
        // Don't show error to user as this is a background process
      }
    }
  };

  useEffect(() => {
    const fetchSubmissionDetails = async () => {
      setLoading(true);
      console.log('Fetching submission details for ID:', submissionId);
      
      if (!submissionId) {
        setError('No submission ID provided');
        setLoading(false);
        return;
      }
      
      try {
        // Construct the full URL for debugging
        const url = getApiUrl(API_ENDPOINTS.SUBMISSION.GET(submissionId));
        console.log('API URL:', url);
        
        const response = await axios.get(
          url,
          { headers: getAuthHeaders() }
        );
        
        if (!response.data) {
          throw new Error('No data returned from API');
        }
        
        console.log('API Response:', response.data);
        const submissionData = response.data.data || response.data;
        console.log('Submission Data:', submissionData);
        
        if (submissionData) {
          setDetails(submissionData);
          // Attempt to mark the submission if it's pending
          await markSubmission(submissionData);
        } else {
          throw new Error('Empty or invalid submission data');
        }
      } catch (err) {
        console.error('Error fetching details:', err);
        setError(`Failed to load submission details: ${err.message || 'Unknown error'}`);
        if (err.response) {
          console.error('Response data:', err.response.data);
          console.error('Response status:', err.response.status);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissionDetails();
  }, [submissionId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="submission-details-container">
      <div className="loading">Loading submission details...</div>
    </div>;
  }

  if (error) {
    return <div className="submission-details-container">
      <div className="error">{error}</div>
    </div>;
  }

  if (!details) {
    return <div className="submission-details-container">
      <div className="error">No submission details found</div>
    </div>;
  }

  return (
    <div className="submission-details-container">
      <div className="submission-container">
        <div className="submission-info-card">
          <div className="submission-header">
            <h2 className="submission-title">Submission Details</h2>
            <PDFExportButton 
              submissionData={details} 
              isTeacherView={false}
              size="2"
              className="ml-auto"
            />
          </div>
          <div className="submission-meta">
            <div className="meta-item">
              <span className="meta-label">Submitted on:</span>
              <span className="meta-value">{formatDate(details.submittedAt)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Status:</span>
              <span className="meta-value status">{details.status}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Total Score:</span>
              <span className="meta-value score">
                {details.status === 'pending' ? 'Pending' : details.totalScore}
              </span>
            </div>
          </div>
        </div>

        <div className="questions-list">
          {details.submissions?.map((submission, index) => (
            <div key={index} className="question-card">
              <div className="question-content">
                <div className="question-header">
                  <h3>Question {index + 1}</h3>
                </div>
                <div className="question-text">
                  {submission.questionId?.question}
                </div>
                <div className="feedback-section">
                  <div className="feedback-label">Answer:</div>
                  <div className="feedback-text">
                    {submission.answer}
                  </div>
                </div>
                {details.status !== 'pending' && (
                  <>
                    <div className="score-section">
                      <div className="score-label">Score:</div>
                      <div className="score-value">
                        {submission.score || 0}
                      </div>
                    </div>
                    {submission.feedback && (
                      <div className="feedback-section">
                        <div className="feedback-label">Feedback:</div>
                        <div className="feedback-text">
                          {Array.isArray(submission.feedback) ? (
                            submission.feedback.map((feedbackItem, idx) => (
                              <div key={idx} className="feedback-item">
                                {feedbackItem.aiComment && (
                                  <div className="ai-comment">{feedbackItem.aiComment}</div>
                                )}
                                {feedbackItem.suggestion && (
                                  <div className="suggestion">Suggestion: {feedbackItem.suggestion}</div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="feedback-item">
                              <div className="ai-comment">
                                {submission.feedback.aiComment || 
                                 (typeof submission.feedback === 'string' ? submission.feedback : 'No feedback available')}
                              </div>
                              {submission.feedback.suggestion && (
                                <div className="suggestion">
                                  Suggestion: {submission.feedback.suggestion}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <button 
          onClick={() => navigate('/my-submissions')} 
          className="back-button"
        >
          Back to Submissions
        </button>
      </div>
    </div>
  );
};

export default SubmissionDetails; 